import { faChevronRight, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { Button, FloatingLabel, Form, InputGroup } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import AlertBox from "../../components/Messages";
import { useLoginMutation } from "../../services/API/titanApi";
import { selectCurrentToken, setCredentials } from "../../store/slices/auth/authSlice";
import {
  setCreatedAt,
  setLogoImageBase64Url,
  setPreparedByCompany,
  setPreparedByUsername,
} from "../../store/slices/deckblattSlice";
import BasePage from "../BasePage/BasePage";
import "./login.scss";
import LoginBubble from "./LoginBubble/LoginBubble";
import titanLogo from "../../images/Ischebeck_Logo2013_VarA_RGB_1zu1_trans.svg";
import { useGetProfileLogoQuery } from "../../services/API/ProfileAPI";

function Login({ initialLang }) {
  const { i18n, t } = useTranslation(["translation", "login"]);

  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // #region URL redirect
  // Remove if https://www.ischebeck.de/service/smart-titan/ points to the correct url
  // ie. no more '?action=register&lang='

  //  /register       => /anmelden?action=register&lang=en
  //  /registrieren   => /anmelden?action=register&lang=
  //  /login          => /anmelden?lang=en&
  //  /anmelden       => /anmelden

  const [queryParameter, setQueryParameter] = useSearchParams();
  const action = queryParameter.get("action");
  const lang = queryParameter.get("lang");

  if (action == "register") {
    if (lang == "en") {
      navigate("/register");
    } else {
      navigate("/registrieren");
    }
  } else if (lang == "en") {
    navigate("/login");
  }
  // #endregion

  const [isPasswordVisible, setisPasswordVisible] = useState(false);

  const [formState, setFormState] = useState({
    username: "",
    password: "",
  });

  const [login, { isLoading }] = useLoginMutation();
  // The profile logo lives behind an authenticated endpoint. Skip it until the
  // user is logged in — otherwise the login page requests it without a token
  // and the resulting 403 surfaces as a console error on every visit.
  const token = useSelector(selectCurrentToken);
  const { data: profileData } = useGetProfileLogoQuery(undefined, { skip: !token });
  const [errMsg, setErrMsg] = useState("");

  const handleChange = ({ target: { name, value } }) => {
    return setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isLoading) {
      return;
    }

    try {
      const userData = await login(formState).unwrap();

      dispatch(setCredentials({ ...userData, user: userData?.username }));
      dispatch(setPreparedByCompany(userData?.company_name));
      dispatch(setPreparedByUsername(userData?.vorename + " " + userData?.surname));

      dispatch(setCreatedAt(new Date(Date.now()).toLocaleDateString(i18n.language)));
      dispatch(setLogoImageBase64Url(profileData?.logoBase64Url));

      setFormState({});

      if (location?.state?.from) {
        return navigate(location?.state?.from);
      }
      navigate("/application");
    } catch (err) {
      if (!err?.status) {
        setErrMsg(t("error.noServerResponse", { ns: "login" }));
      } else if (err.status === 400) {
        setErrMsg(t("error.noUsernameOrPassword", { ns: "login" }));
      } else if (err.status === 401) {
        setErrMsg(t("error.unauthorized", { ns: "login" }));
      } else if (err.status === 423) {
        setErrMsg(t("error.inactiveUser", { ns: "login" }));
      } else if (err.status === 901) {
        setErrMsg(t("error.unverifiedEmail", { ns: "login" }));
      } else {
        setErrMsg(t("login.failed"));
      }
    }
  };

  useEffect(() => {
    setErrMsg("");
  }, [formState]);

  const [langWasModified, setlangWasModified] = useState(false);

  // Initialize language based on URL on component mount
  useEffect(() => {
    if (!langWasModified) {
      i18n.changeLanguage(initialLang).then(() => {
        setlangWasModified(true);
      });
    }
  }, [initialLang, i18n, langWasModified]);

  // Update URL when language changes
  useEffect(() => {
    if (langWasModified) {
      let route = "/anmelden"; // default German

      if (i18n.language === "en") {
        route = "/login";
      } else if (i18n.language === "es-ES") {
        route = "/conectarse";
      }

      // Only navigate if not already on the correct route
      if (location.pathname !== route) {
        navigate(route, { replace: true });
      }
    }
  }, [i18n.language, navigate, location.pathname, langWasModified]);

  return (
    <BasePage size={8} bubble={<LoginBubble />}>
      <section className="login-base">
        <div className="login-top-logo-wrapper">
          <img className="login-top-logo" src={titanLogo} alt="" />
        </div>
        <form>
          <div className="p-lg-6 p-md-3 p-3 mx-lg-4 mt-lg-5">
            <h1 className="my-lg-6 pb-1 login-headline">{t("login.title")}</h1>
            <br />

            <FloatingLabel
              className="my-5"
              controlId="InputUsername"
              label={t("placeholder.username", { ns: "login" })}
            >
              <Form.Control
                onChange={handleChange}
                value={formState.name}
                type="text"
                className="login-border login-width"
                placeholder={t("placeholder.username", { ns: "login" })}
                name="username"
                required
              />
            </FloatingLabel>

            <InputGroup className="mb-3 my-5 login-width">
              <FloatingLabel
                controlId="InputPassword"
                label={t("placeholder.password", { ns: "login" })}
              >
                <Form.Control
                  className="login-border"
                  onChange={handleChange}
                  value={formState.name}
                  type={isPasswordVisible ? "text" : "password"}
                  placeholder={t("placeholder.password", { ns: "login" })}
                  name="password"
                  required
                  autoComplete="off"
                />
              </FloatingLabel>
              <Button
                className="login-border"
                variant="outline-secondary"
                id="button-addon2"
                onClick={() => {
                  setisPasswordVisible(!isPasswordVisible);
                }}
              >
                {isPasswordVisible ? <FaEye /> : <FaEyeSlash />}
              </Button>
            </InputGroup>
            <Link className="forgot-password" to={"/resetpassword"}>
              {t("resetPassword", { ns: "login" })}
            </Link>

            <br />
            <div className="pt-1 my-5 pb-1">
              <button className="login-button" type="button" onClick={handleSubmit}>
                <div className="verticallyAligned">
                  <div>{t("login.login")}</div>
                  <div style={{ marginLeft: "25px" }}>
                    {isLoading ? (
                      <FontAwesomeIcon color="yellow" icon={faSpinner} className={"fa-spin"} />
                    ) : (
                      <FontAwesomeIcon color="yellow" icon={faChevronRight} />
                    )}
                  </div>
                </div>
              </button>

              <AlertBox show={location?.state?.errorMsg} message={location?.state?.errorMsg} />

              <AlertBox show={errMsg !== ""} message={errMsg} />
            </div>
            <br />
            <div className="login-linkTextContainer">
              <Link
                className="login-linkText"
                to={
                  i18n.language === "en"
                    ? "/register"
                    : i18n.language === "es"
                      ? "/registrese"
                      : "/registrieren"
                }
              >
                {t("login.register")}
              </Link>
            </div>
          </div>
        </form>
      </section>
    </BasePage>
  );
}

export default Login;
